import { Box, Flex } from "@chakra-ui/layout";
import { Table, TableProps } from "antd";
import { ColumnType } from "antd/lib/table";
import { useEffect, useState, useMemo } from "react";
import Pagination from "../Pagination";
import { TableContainer } from "./styles";
import { checkPermission } from "infra/helpers/permission";
import styled from "styled-components";
import BottomTableFixed, {
  ActionBottomType,
} from "components/BottomTableFixed";
import * as _ from "lodash";
import ActionsKebabMenu from "./../ActionsKebabMenu";
import { useRef } from "react";

const Scroll = styled.div`
  .ant-table td {
    width: fit-content;
    overflow: hidden !important;
  }
  .ant-table .ant-table-body {
    overflow-y: auto !important;
  }
`;
export interface NewTableProps<T> {
  hideShadow?: boolean;
  CustomFooter?: React.ReactNode;
  actions?: {
    invisible?: boolean;
    title: string | ((_v: T) => any);
    permission?: string | ((_v: T) => string);
    icon?: (_v: T) => any;
    onClick: (_v: T, index?: number) => void;
    shouldRender?: (original: T) => boolean;
    color?: string;
  }[];
  customPagination?: {
    onChangePage: (p: number, perPage: number) => void;
    currentPage?: number;
    itemsPerPage?: number;
    totalItems?: number;
  };
  customSelectedRows?: {
    handler: (v: T[]) => void;
    selecteds: T[];
    actions: ActionBottomType[];
  };
  highlight?: string;
  highlightProp?: string;
  showCustomSelectionActions?: boolean;
  maxSelectedRows?: number;
  defaultSelectedRows?: T[];
  actionsContainerRef?: React.RefObject<HTMLElement | null> | undefined;
  selectedRowsKeyExtractor?: (selectedRows: T[]) => string[];
}

function NewTable<T = any>({
  customPagination,
  actions,
  hideShadow,
  CustomFooter,
  customSelectedRows,
  showCustomSelectionActions = true,
  maxSelectedRows,
  actionsContainerRef,
  defaultSelectedRows = [],
  selectedRowsKeyExtractor,
  highlight,
  highlightProp = "id",
  ...props
}: NewTableProps<T> & Partial<TableProps<any>>) {
  const [selectedRows, setSelectedRows] = useState<T[]>(defaultSelectedRows);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (
      props.dataSource &&
      props.dataSource.length === 0 &&
      customPagination?.currentPage &&
      customPagination.currentPage > 1
    ) {
      customPagination?.onChangePage(1, customPagination.itemsPerPage ?? 10);
    }
  }, [props.dataSource, customPagination]);
  let _columns: ColumnType<T>[] =
    props.columns?.map((c) => ({
      shouldCellUpdate: (record, prevRecord) => !_.isEqual(record, prevRecord),
      ...c,
    })) ?? [];

  actions =
    actions?.filter((action) => {
      if (typeof action.permission === "string") {
        return checkPermission(action.permission);
      }

      if (action.invisible) {
        return false;
      }

      return true;
    }) ?? [];

  if (actions?.length) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _columns = [
      ..._columns,
      {
        title: "",
        width: "70px",
        align: "right",
        render: (original: T, record?: T, indexOriginal?: number) => {
          return (
            <Flex justifyContent="flex-end" overflow="visible">
              <ActionsKebabMenu
                indexItem={indexOriginal}
                item={original}
                actions={actions}
                actionsContainerRef={actionsContainerRef ?? tableContainerRef}
              />
            </Flex>
          );
        },
      } as any,
    ];
  }

  const customProps: any = useMemo(() => {
    let result: any = {};

    if (
      customSelectedRows &&
      typeof customSelectedRows.handler === "function"
    ) {
      result.rowSelection = {
        type: "checkbox",
        preserveSelectedRowKeys: true,
        onChange: (_event: any, v: T[]) => {
          let rows = v;

          if (maxSelectedRows) {
            rows =
              v.length > 2 ? [] : rows.slice(rows.length - maxSelectedRows);
          }

          customSelectedRows.handler(rows);
          setSelectedRows(rows);
        },
        selectedRowKeys: selectedRowsKeyExtractor
          ? selectedRowsKeyExtractor(selectedRows)
          : selectedRows.map((item: any) => item.id),
        hideSelectAll: maxSelectedRows
          ? maxSelectedRows < (customPagination?.itemsPerPage || 10)
          : false,
      };
    }

    return result;
  }, [
    customPagination?.itemsPerPage,
    customSelectedRows,
    maxSelectedRows,
    selectedRows,
    selectedRowsKeyExtractor,
  ]);

  return (
    <>
      <TableContainer
        hideShadow={hideShadow}
        flatBottom={!!CustomFooter}
        ref={tableContainerRef}
      >
        <Box
          boxShadow={
            hideShadow ? undefined : "0 0 16px 3px rgba(0, 0, 0, 0.05)"
          }
          rounded="16px"
          mb="32px"
        >
          <Scroll>
            <Table
              style={{
                borderRadius: "16px",
                borderBottomLeftRadius: CustomFooter ? 0 : "16px",
                borderBottomRightRadius: CustomFooter ? 0 : "16px",
              }}
              pagination={false}
              rowKey="id"
              scroll={{ x: true }}
              rowClassName={(row) =>
                !!row[highlightProp] && row[highlightProp] === highlight
                  ? "ant-table-row-highlighted"
                  : undefined
              }
              {...customProps}
              {...props}
              columns={_columns}
            />
            {CustomFooter}
          </Scroll>

          {typeof customPagination?.onChangePage === "function" ? (
            <Pagination
              style={{ margin: 16 }}
              current={customPagination?.currentPage ?? 1}
              total={customPagination?.totalItems ?? 0}
              pageSize={customPagination?.itemsPerPage ?? 0}
              onChange={(page, pageSize) => {
                customPagination.onChangePage(page, pageSize);
              }}
            />
          ) : null}
        </Box>
      </TableContainer>

      {selectedRows?.length &&
      customSelectedRows &&
      showCustomSelectionActions ? (
        <BottomTableFixed
          selectedRows={selectedRows}
          onClose={() => {
            setSelectedRows([]);
            customSelectedRows.handler([]);
          }}
          actions={customSelectedRows?.actions!}
        />
      ) : null}
    </>
  );
}

export default NewTable;
